/**
 * src/utils/undoRedo/commands/GizmoEditCommand.js
 * Gizmo ile vertex/geometri düzenleme komutu
 * Line, circle, arc, ellipse destekler
 */

import * as THREE from 'three';
import BaseCommand from './BaseCommand';
import { clearSelection } from '../../../redux/selectionSlice';

class GizmoEditCommand extends BaseCommand {
  /**
   * @param {string} objectId - Düzenlenen nesnenin userData.id'si
   * @param {Object} editData - { objectType, before: { vertices, userData, position }, after: { vertices, userData, position } }
   */
  constructor(objectId, editData) {
    super('GIZMO_EDIT', {
      before: editData.before,
      after: editData.after
    }, { objectId, objectType: editData.objectType });
    this.objectId = objectId;
  }

  /**
   * Redo: after state'ini uygula
   */
  execute(scene, dispatch) {
    this._applyState(scene, this.data.after);
    if (dispatch) dispatch(clearSelection());
  }

  /**
   * Undo: before state'ini uygula
   */
  undo(scene, dispatch) {
    this._applyState(scene, this.data.before);
    if (dispatch) dispatch(clearSelection());
  }

  _applyState(scene, state) {
    scene.traverse(obj => {
      if (obj.userData?.id !== this.objectId) return;

      // Vertex'leri geri yükle
      if (state.vertices && obj.geometry?.attributes?.position) {
        const pos = obj.geometry.attributes.position;
        state.vertices.forEach((v, i) => {
          if (i < pos.count) {
            pos.setXYZ(i, v.x, v.y, v.z);
          }
        });
        pos.needsUpdate = true;
        obj.geometry.computeBoundingSphere();
      }

      // Position'ı geri yükle
      if (state.position) {
        obj.position.set(state.position.x, state.position.y, state.position.z);
      }

      // Scale'i geri yükle
      if (state.scale) {
        obj.scale.set(state.scale.x, state.scale.y, state.scale.z);
      }

      // userData'yı geri yükle
      if (state.userData) {
        Object.assign(obj.userData, state.userData);
      }

      // Sprite MTEXT: scale değişince canvas texture'ı yeniden çiz
      if (this.meta.objectType === 'mtext' && state.scale && obj.isSprite) {
        this._rerenderMtextSprite(obj);
      }
    });
  }

  _rerenderMtextSprite(sprite) {
    const ud = sprite.userData;
    if (!ud || !ud.rawText) return;

    const baseFontSize = parseInt(ud.baseFontSize || '20', 10);
    const scaleFactor = baseFontSize / 20;
    const font = ud.font || 'Arial';
    const bold = ud.bold || false;
    const italic = ud.italic || false;
    const textColor = ud.color || '#ffffff';

    const width = sprite.scale.x * scaleFactor;
    const height = sprite.scale.y * scaleFactor;

    const dpr = window.devicePixelRatio || 1;
    const resolutionFactor = 4;

    const fontStyle = `${italic ? 'italic ' : ''}${bold ? 'bold ' : ''}${baseFontSize}px ${font}`;
    const lineHeight = baseFontSize * 1.4;
    const padding = 8;
    const maxTextWidth = Math.max(1, width - padding * 2);

    // Ölçüm için geçici canvas
    const canvas = document.createElement('canvas');
    canvas.width = width * dpr * resolutionFactor;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr * resolutionFactor, dpr * resolutionFactor);
    ctx.font = fontStyle;

    const lines = [];
    for (const paragraph of ud.rawText.split('\n')) {
      const words = paragraph.split(/\s+/);
      let line = '';
      for (const word of words) {
        const testLine = line ? line + ' ' + word : word;
        if (ctx.measureText(testLine).width > maxTextWidth && line) {
          lines.push(line);
          line = word;
        } else {
          line = testLine;
        }
      }
      if (line) lines.push(line);
    }

    // Yazı taşarsa canvas aşağı doğru büyüsün
    const neededHeight = lineHeight * lines.length + padding * 2;
    const actualHeight = Math.max(height, neededHeight);

    // Gerçek boyutla canvas'ı oluştur
    canvas.width = width * dpr * resolutionFactor;
    canvas.height = actualHeight * dpr * resolutionFactor;
    ctx.scale(dpr * resolutionFactor, dpr * resolutionFactor);
    ctx.font = fontStyle;
    ctx.textBaseline = 'top';

    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, width, neededHeight);

    ctx.fillStyle = textColor;
    lines.forEach((line, i) => {
      ctx.fillText(line, padding, padding + i * lineHeight);
    });

    // Eski texture'ı dispose et, yeni texture oluştur
    if (sprite.material.map) sprite.material.map.dispose();
    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 16;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    sprite.material.map = texture;
    sprite.material.needsUpdate = true;

    // Scale'i canvas/scaleFactor oranıyla ayarla
    sprite.scale.set(
      canvas.width / (scaleFactor * dpr * resolutionFactor),
      canvas.height / (scaleFactor * dpr * resolutionFactor),
      1
    );

    ud.width = width;
    ud.height = actualHeight;
  }
}

export default GizmoEditCommand;
